/**
 * Money in the driver app, in the right currency (item 3, 2026-10-02).
 *
 * Warren: "Canadian home tariffs are priced, displayed, charged and paid
 * out in CAD; US homes in USD." Every home guest and host surface formats
 * through here, with the currency the API sends beside the amount
 * (tariff.currency, cost.currency, home_monetization.currency, ...) and
 * the app language, so English shows "$0.45" for USD and "CA$0.45" for
 * CAD, and French "0,45 $US" and "0,45 $CA". Intl does the symbol and
 * the separators; nothing here hardcodes '$'.
 *
 * The three shapes the app already used are kept, per currency:
 *   formatMoney         "$2.50", "CA$2.50" (always two decimals)
 *   formatMoneyCompact  "45¢" under a dollar where the currency's symbol
 *                       in this language is a bare "$" (USD in English),
 *                       else the full form ("CA$0.45"): "45¢" never says
 *                       which dollar it is. A fraction of a cent is kept,
 *                       never rounded ("32.99¢", "CA$0.275")
 *   formatMoneyTenths   tenths of a cent, three decimals only when the
 *                       tenths digit is not zero ("$0.255", "$0.25"): the
 *                       host's 85% of a per-kWh price
 *   formatFee           whole units bare ("$1", "CA$1"), else two decimals
 *   formatMoneySegments formatMoney in two pieces, currency and number,
 *                       for a narrow card that must wrap between them
 *
 * Pure (no React, no i18n import), so node tests run it directly. Pages
 * pass `i18n.language`.
 */

/** The currency when a payload predates the field: every amount before
 *  item 3 was USD. */
export const DEFAULT_CURRENCY = 'USD';

/** An ISO 4217 code Intl accepts, uppercase; DEFAULT_CURRENCY otherwise. */
export function moneyCurrency(currency: string | null | undefined): string {
  const c = typeof currency === 'string' ? currency.trim().toUpperCase() : '';
  return /^[A-Z]{3}$/.test(c) ? c : DEFAULT_CURRENCY;
}

/** The app language as an Intl locale; English when it is unusable. */
function moneyLocale(locale: string | null | undefined): string {
  const l = typeof locale === 'string' && locale.trim() ? locale.trim() : 'en';
  try {
    return Intl.NumberFormat.supportedLocalesOf([l]).length > 0 ? l : 'en';
  } catch {
    return 'en';
  }
}

const cache = new Map<string, Intl.NumberFormat>();
function formatter(
  currency: string, locale: string, fractionDigits: number, maxFractionDigits: number = fractionDigits,
): Intl.NumberFormat {
  const key = `${locale}|${currency}|${fractionDigits}|${maxFractionDigits}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: maxFractionDigits,
    });
    cache.set(key, f);
  }
  return f;
}

/** "$2.50" / "CA$2.50": cents in `currency`, two decimals. */
export function formatMoney(cents: number, currency?: string | null, locale?: string | null): string {
  return formatter(moneyCurrency(currency), moneyLocale(locale), 2).format(cents / 100);
}

/**
 * formatMoney that always says which dollar: "US$3.50" where the language's
 * USD symbol is a bare "$" (English), so a wallet in one currency beside a
 * price in another can never be misread (2026-10-25). "CA$3.50" and
 * "3,50 $US" are already explicit and pass through unchanged.
 */
export function formatMoneyExplicit(cents: number, currency?: string | null, locale?: string | null): string {
  const c = moneyCurrency(currency);
  const parts = formatter(c, moneyLocale(locale), 2).formatToParts(cents / 100);
  return parts.map((p) => (p.type === 'currency' && p.value === '$' && c === 'USD' ? 'US$' : p.value)).join('');
}

/** A rate per kWh or per hour: "$0.18", and up to four decimals when it holds a fraction of a cent, "$0.4363",
 *  "CA$0.4363" (a location's rate is kept to a hundredth of a cent: Warren, 21 October 2026, night). */
export function formatMoneyRate(cents: number, currency?: string | null, locale?: string | null): string {
  return formatter(moneyCurrency(currency), moneyLocale(locale), 2, 4).format(cents / 100);
}

/** The currency's symbol in this language: "$" for USD in English,
 *  "CA$" for CAD, "$US" for USD in French. */
export function currencySymbol(currency?: string | null, locale?: string | null): string {
  const parts = formatter(moneyCurrency(currency), moneyLocale(locale), 0).formatToParts(1);
  return parts.find((p) => p.type === 'currency')?.value ?? moneyCurrency(currency);
}

/**
 * "45¢" under a dollar when the symbol is a bare "$", else formatMoney.
 *
 * A rate can hold a fraction of a cent (the admin tariff form takes
 * cents per kWh and per minute to 0.01), and under a dollar it is shown
 * whole, never rounded: "32.99¢", "0.5¢/min", as the pre-session page
 * always printed it. In the full form the same amount keeps its digits
 * too, up to four decimals ("CA$0.275"), so a 0.5¢ idle fee never reads
 * as "$0.01", double the real rate. A dollar or more rounds to cents,
 * as before.
 */
export function formatMoneyCompact(cents: number, currency?: string | null, locale?: string | null): string {
  if (cents >= 0 && cents < 100) {
    if (currencySymbol(currency, locale) === '$') {
      // toFixed(4) then Number drops float noise (0.1 + 0.2) and any
      // trailing zeros, so 27.5 prints "27.5¢" and 45 prints "45¢".
      return `${Number(cents.toFixed(4))}¢`;
    }
    if (!Number.isInteger(cents)) {
      return formatter(moneyCurrency(currency), moneyLocale(locale), 2, 4).format(cents / 100);
    }
  }
  return formatMoney(cents, currency, locale);
}

/**
 * formatMoney split where a line may break: between the currency and the
 * number. ["CA$", "123.45"] for a symbol in front, ["123,45", "CAD"] for
 * one behind (the space Intl puts there is a no-break space, so a narrow
 * card could never wrap it). A caller renders the two with a break
 * opportunity between them; joined, they read as formatMoney does.
 * `symbolFirst` says which side the currency is on.
 */
export function formatMoneySegments(
  cents: number, currency?: string | null, locale?: string | null,
): { first: string; second: string; symbolFirst: boolean } {
  const parts = formatter(moneyCurrency(currency), moneyLocale(locale), 2).formatToParts(cents / 100);
  const at = parts.findIndex((p) => p.type === 'currency');
  const text = (xs: Intl.NumberFormatPart[]) => xs.map((p) => p.value).join('').trim();
  if (at < 0) return { first: text(parts), second: '', symbolFirst: false };
  const numberStart = parts.findIndex((p) => p.type === 'integer');
  const symbolFirst = numberStart < 0 || at < numberStart;
  return symbolFirst
    ? { first: text(parts.slice(0, at + 1)), second: text(parts.slice(at + 1)), symbolFirst }
    : { first: text(parts.slice(0, at)), second: text(parts.slice(at)), symbolFirst };
}

/**
 * Tenths of a cent: three decimals only when the tenths digit is not
 * zero, so 255 is "$0.255" but 250 is "$0.25". The magnitude only; the
 * caller places a sign. Integer tenths in, so the third decimal is exact.
 */
export function formatMoneyTenths(tenths: number, currency?: string | null, locale?: string | null): string {
  const abs = Math.abs(Math.round(tenths));
  const digits = abs % 10 === 0 ? 2 : 3;
  return formatter(moneyCurrency(currency), moneyLocale(locale), digits).format(abs / 1_000);
}

/** A fixed fee: "$1", "CA$1" for whole units, else "$0.50". Warren's
 *  wording is "a $1 connection fee", not "a $1.00 connection fee". */
export function formatFee(cents: number, currency?: string | null, locale?: string | null): string {
  return cents % 100 === 0
    ? formatter(moneyCurrency(currency), moneyLocale(locale), 0).format(cents / 100)
    : formatMoney(cents, currency, locale);
}

/** A fixed formatting context: the currency and language a surface uses
 *  for every amount, so a page builds it once. */
export interface MoneyFormat {
  currency: string;
  locale: string;
  money(cents: number): string;
  compact(cents: number): string;
  tenths(tenths: number): string;
  fee(cents: number): string;
  symbol: string;
}

export function moneyFormat(currency?: string | null, locale?: string | null): MoneyFormat {
  const c = moneyCurrency(currency);
  const l = moneyLocale(locale);
  return {
    currency: c,
    locale: l,
    money: (cents) => formatMoney(cents, c, l),
    compact: (cents) => formatMoneyCompact(cents, c, l),
    tenths: (tenths) => formatMoneyTenths(tenths, c, l),
    fee: (cents) => formatFee(cents, c, l),
    symbol: currencySymbol(c, l),
  };
}
